<template>
  <div class="project-container">
    <!-- Hero Section -->
    <header class="hero-section">
      <div class="hero-content">
        <div class="hero-badge">Infrastructure & SecOps</div>
        <h1 class="hero-title">Home Lab Cloud</h1>
        <p class="hero-subtitle">Cloud Privé de Niveau Production & Orchestration</p>
        <div class="hero-meta">
          <span class="meta-item"><i class="mdi mdi-server-network"></i> Cluster K3s HA</span>
          <span class="meta-item"><i class="mdi mdi-shield-lock"></i> Identity & SSO</span>
          <span class="meta-item"><i class="mdi mdi-sync"></i> GitOps Flow</span>
        </div>
      </div>
      <div class="hero-image">
         <i class="mdi mdi-server-security" style="font-size: 150px; color: var(--primary-color); opacity: 0.8;"></i>
      </div>
    </header>

    <!-- Context / Overview -->
    <section class="card overview-card">
      <div class="card-header">
        <i class="mdi mdi-information-outline"></i>
        <h2>Contexte & Vision</h2>
      </div>
      <div class="card-content">
        <p class="lead-text">
          Mon <strong>Home Lab</strong> est un écosystème cloud-native complet, conçu pour répondre aux exigences de haute disponibilité et de sécurité des environnements d'entreprise modernes.
        </p>
        <p>
          Ce projet constitue le pilier technique de ma spécialisation en <strong>SecOps</strong>. Il prépare activement mon futur <strong>Master en Cybersécurité à l'UQAC</strong> (Québec) en me permettant d'expérimenter concrètement le déploiement continu (GitOps), la résilience des systèmes distribués et la sécurisation granulaire des accès.
        </p>
         <div class="tech-stack">
          <span class="tech-badge">Kubernetes (K3s)</span>
          <span class="tech-badge">Proxmox VE</span>
          <span class="tech-badge">ArgoCD</span>
          <span class="tech-badge">Zitadel SSO</span>
          <span class="tech-badge">Longhorn CSI</span>
          <span class="tech-badge">ZFS Storage</span>
        </div>
      </div>
    </section>

    <!-- Architecture Visualisation -->
    <section class="card arch-card">
      <div class="card-header">
        <i class="mdi mdi-sitemap"></i>
        <h2>Architecture Réseau</h2>
      </div>
      <div class="card-content">
        <div class="svg-container">
           <svg viewBox="0 0 1400 1100" xmlns="http://www.w3.org/2000/svg" class="network-topology">
              <defs>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>
              <g class="layer proxmox-layer">
                <rect x="560" y="30" width="280" height="100" rx="12" fill="var(--dark-bg-secondary)" stroke="var(--primary-color)" stroke-width="3"/>
                <text x="700" y="60" class="svg-text title">Proxmox VE 8.x</text>
                <text x="700" y="80" class="svg-text subtitle">Hypervisor QEMU/KVM</text>
                <text x="700" y="100" class="svg-text detail">HA Clustering</text>
              </g>
              <g class="node k3s-master">
                <rect x="200" y="180" width="180" height="110" rx="10" fill="var(--card-background)" stroke="var(--secondary-color)" stroke-width="2.5"/>
                <text x="290" y="210" class="svg-text title">K3s Master 1</text>
                <text x="290" y="230" class="svg-text detail">etcd</text>
              </g>
              <g class="node k3s-master">
                <rect x="610" y="180" width="180" height="110" rx="10" fill="var(--card-background)" stroke="var(--secondary-color)" stroke-width="2.5"/>
                <text x="700" y="210" class="svg-text title">K3s Master 2</text>
              </g>
              <g class="node k3s-master">
                <rect x="1020" y="180" width="180" height="110" rx="10" fill="var(--card-background)" stroke="var(--secondary-color)" stroke-width="2.5"/>
                <text x="1110" y="210" class="svg-text title">K3s Master 3</text>
              </g>
              <g class="service kube-vip">
                <rect x="530" y="340" width="340" height="70" rx="10" fill="var(--dark-bg-secondary)" stroke="var(--primary-color)" stroke-width="3"/>
                <text x="700" y="370" class="svg-text title">Kube VIP</text>
              </g>
              <g class="service traefik">
                <rect x="950" y="440" width="240" height="90" rx="10" fill="var(--dark-bg-secondary)" stroke="var(--primary-color)" stroke-width="3"/>
                <text x="1070" y="470" class="svg-text title">Traefik Ingress</text>
              </g>
              <line x1="700" y1="130" x2="290" y2="180" class="connection-line" />
              <line x1="700" y1="130" x2="700" y2="180" class="connection-line" />
              <line x1="700" y1="130" x2="1110" y2="180" class="connection-line" />
            </svg>
        </div>
      </div>
    </section>

    <!-- Details Grid -->
    <section class="card details-card">
       <div class="card-header">
        <i class="mdi mdi-cogs"></i>
        <h2>Stack Technique & Services</h2>
      </div>
      <div class="feature-grid">
         <div class="feature-item">
            <div class="feature-icon"><i class="mdi mdi-virtual-reality"></i></div>
            <h4>Virtualisation & Stockage</h4>
            <p>Hyperviseur Proxmox VE 8.x exploitant ZFS pour la compression et la redondance. Architecture en cluster HA avec segmentation réseau par VLANs.</p>
         </div>
         <div class="feature-item">
            <div class="feature-icon"><i class="mdi mdi-kubernetes"></i></div>
            <h4>Orchestration K8s</h4>
            <p>Distribution K3s légère pilotant un cluster multi-nœuds. Persistance des données via Longhorn (réplication distribuée).</p>
         </div>
         <div class="feature-item">
            <div class="feature-icon"><i class="mdi mdi-shield-account"></i></div>
            <h4>Gestion d'Identité (IAM)</h4>
            <p>Déploiement de **Zitadel** (SSO) pour centraliser l'authentification via OIDC et SAML.</p>
         </div>
      </div>
    </section>

    <!-- IUT Competencies Validation -->
    <section class="card validation-card">
      <div class="card-header">
        <i class="mdi mdi-certificate-outline"></i>
        <h2>Validation des Compétences IUT</h2>
      </div>
      <div class="card-content">
        <div class="validation-grid">
          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">AC 1</span>
              <h4>Administrer : Automatisation</h4>
            </div>
            <p><strong>Pourquoi :</strong> Gérer une infrastructure complexe de manière prévisible et scalable sans interventions manuelles répétitives.</p>
            <p><strong>Comment :</strong> Mise en place d'un flux <strong>GitOps</strong> avec <strong>ArgoCD</strong> pour synchroniser l'état du cluster avec les dépôts Git (IaC).</p>
          </div>

          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">AC 2</span>
              <h4>Administrer : Configuration Avancée</h4>
            </div>
            <p><strong>Pourquoi :</strong> Créer un environnement multi-tenant sécurisé où chaque service est isolé mais accessible.</p>
            <p><strong>Comment :</strong> Utilisation de <strong>K3s</strong>, configuration de <strong>MetalLB</strong> pour le load balancing et <strong>Traefik</strong> pour le routage Ingress.</p>
          </div>

          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">AC 3</span>
              <h4>Administrer : Sécurité Infrastructure</h4>
            </div>
            <p><strong>Pourquoi :</strong> Centraliser le contrôle d'accès et sécuriser les communications dans un cluster ouvert sur l'extérieur.</p>
            <p><strong>Comment :</strong> Déploiement de <strong>Zitadel SSO</strong>, gestion automatique des certificats via <strong>Cert-Manager</strong> et Network Policies strictes.</p>
          </div>

          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">CE3.04</span>
              <h4>Administrer : Continuité d'Activité</h4>
            </div>
            <p><strong>Pourquoi :</strong> Garantir que les services auto-hébergés restent fonctionnels malgré les pannes de nœuds ou de disques.</p>
            <p><strong>Comment :</strong> Cluster K3s en mode <strong>High Availability</strong> (3 masters) et stockage distribué répliqué avec <strong>Longhorn CSI</strong>.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Learning Outcomes -->
    <section class="card learning-card">
       <div class="card-header">
        <i class="mdi mdi-lightbulb-on"></i>
        <h2>Compétences Développées</h2>
      </div>
      <div class="card-content">
        <div class="learning-grid">
          <div class="learning-category">
            <h4>Cloud & DevOps</h4>
            <ul>
              <li>Administration Kubernetes</li>
              <li>GitOps avec ArgoCD</li>
              <li>Infrastructure as Code</li>
            </ul>
          </div>
          <div class="learning-category">
            <h4>Cybersécurité</h4>
            <ul>
              <li>Identity & Access Management</li>
              <li>Sécurisation d'Infrastructure</li>
              <li>Monitoring & Alerting</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';

@Component({})
export default class HomeLabComponent extends Vue {
}
</script>

<style scoped>
.project-container {
  font-family: 'Inter', 'Segoe UI', sans-serif;
  line-height: 1.8;
  color: var(--text-color);
  background-color: var(--background-color);
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  padding-left: 100px;
}

.hero-section {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 40px;
  margin-bottom: 60px;
  padding: 60px 40px;
  background: linear-gradient(135deg, var(--card-background) 0%, var(--dark-bg-secondary) 100%);
  border-radius: 24px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.hero-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.hero-badge {
  display: inline-block;
  width: fit-content;
  padding: 8px 16px;
  background: rgba(19, 124, 139, 0.2);
  color: var(--primary-color);
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 600;
  margin-bottom: 16px;
  border: 1px solid var(--primary-color);
}

.hero-title {
  font-size: 3.5em;
  font-weight: 800;
  color: var(--h1-color);
  margin: 0 0 16px 0;
  background: linear-gradient(135deg, #ffffff 0%, var(--tertiary-color) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  font-size: 1.4em;
  color: var(--sub-color);
  margin-bottom: 24px;
}

.hero-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-muted);
}

.meta-item i {
  color: var(--primary-color);
}

.hero-image {
  display: flex;
  align-items: center;
  justify-content: center;
}

.card {
  background: var(--card-background);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 32px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-4px);
  border-color: var(--primary-color);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--dark-border);
}

.card-header i {
  font-size: 32px;
  color: var(--primary-color);
}

.card-header h2 {
  font-size: 2em;
  color: var(--h2-color);
  margin: 0;
}

.tech-stack {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.tech-badge {
  padding: 10px 20px;
  background: rgba(19, 124, 139, 0.15);
  color: var(--primary-color);
  border-radius: 12px;
  font-size: 0.9em;
  font-weight: 600;
  border: 1px solid rgba(19, 124, 139, 0.3);
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.feature-item {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
}

.feature-icon i {
  font-size: 24px;
  color: var(--primary-color);
}

.svg-container {
  width: 100%;
}

.network-topology {
  width: 100%;
  height: auto;
}

.svg-text {
  text-anchor: middle;
  font-family: 'Inter', sans-serif;
}

.svg-text.title { fill: var(--h1-color); font-size: 16px; font-weight: 700; }
.svg-text.detail { fill: var(--text-muted); font-size: 12px; }

.connection-line {
  stroke: var(--secondary-color);
  stroke-width: 2;
  opacity: 0.6;
}

/* Validation Card Styles */
.validation-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.validation-item {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  border-top: 4px solid var(--primary-color);
}

.v-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.v-code {
  padding: 4px 10px;
  background: var(--primary-color);
  color: #ffffff;
  border-radius: 6px;
  font-weight: 800;
  font-size: 0.9em;
  white-space: nowrap;
}

.v-header h4 {
  margin: 0;
  color: var(--h2-color);
  font-size: 1.1em;
}

.validation-item p {
  font-size: 0.95em;
  margin: 8px 0;
  line-height: 1.6;
}

.validation-item strong {
  color: var(--primary-color);
}

.learning-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.learning-category h4 {
  color: var(--primary-color);
}

.learning-category ul {
  list-style: none;
  padding: 0;
}

.learning-category li::before {
  content: '→';
  color: var(--primary-color);
  margin-right: 8px;
}

@media (max-width: 768px) {
  .project-container { padding-left: 20px; }
  .hero-section { grid-template-columns: 1fr; }
}
</style>